import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { apiClient } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingPage } from './LoadingPage';

interface RouteGuardProps {
  children: React.ReactNode;
}

export const RouteGuard: React.FC<RouteGuardProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isChecking, setIsChecking] = useState(true);

  console.log('RouteGuard: user from useAuth:', user);
  console.log('RouteGuard: gasUserId:', user?.gasUserId);

  // Fetch user profile to check if Chess.com is configured
  const { data: userProfile, isLoading: profileLoading, error } = useQuery(
    ['userProfile', user?.gasUserId],
    () => {
      console.log('RouteGuard: Making getUserProfile API call');
      return apiClient.getUserProfile();
    },
    {
      enabled: !!user?.gasUserId,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        console.log('RouteGuard: getUserProfile success:', data);
      },
      onError: (error) => {
        console.log('RouteGuard: getUserProfile error:', error);
      },
    }
  );

  useEffect(() => {
    const checkAndRedirect = async () => {
      // Wait for user data and profile to load
      if (!user?.gasUserId || profileLoading) {
        return;
      }

      // If there's an error fetching profile, user might not exist yet
      if (error) {
        console.log('Profile fetch error, redirecting to chess-setup');
        if (location.pathname !== '/chess-setup') {
          navigate('/chess-setup');
        }
        setIsChecking(false);
        return;
      }

      // Check if user has Chess.com profile configured
      const hasChessProfile = userProfile?.data?.chessUsername;
      
      if (!hasChessProfile) {
        // User doesn't have Chess.com profile, redirect to setup
        console.log('No Chess.com profile found, redirecting to chess-setup');
        if (location.pathname !== '/chess-setup') {
          navigate('/chess-setup');
        }
      } else {
        // User has Chess.com profile, redirect to leaderboard if on root
        console.log('Chess.com profile found, redirecting to leaderboard');
        if (location.pathname === '/') {
          navigate('/leaderboard');
        }
      }
      
      setIsChecking(false);
    };

    checkAndRedirect();
  }, [user?.gasUserId, profileLoading, error, userProfile, location.pathname, navigate]);

  // Show loading while checking
  if (isChecking || profileLoading) {
    return <LoadingPage />;
  }

  // Render children once routing is determined
  return <>{children}</>;
}; 